/**
 * Plain-Turkish names for what the engine writes as codes. The loop view is read by people who
 * never saw the engine source, so every code it shows has to say what it means here.
 */

export const TOOL_LABELS: Record<string, string> = {
  search_knowledge: "Bilgi tabanında ara",
  find_skill: "Uygun skill'i bul",
  start_skill: "Skill'i başlat",
  skill_step_done: "Skill adımını tamamla",
  skill_action: "Skill içinden action çalıştır",
  skill_write_action: "Kayıt oluşturan action (onaylı)",
  skill_knowledge: "Skill'in bilgi kaynağında ara",
  skill_update_attribute: "Müşteri bilgisini kaydet",
  skill_handoff: "Skill içinden insana devret",
  skill_run_subskill: "Alt skill çalıştır",
  skill_switch: "Başka skill'e geç",
  skill_complete: "Skill'i bitir",
  skill_cancel: "Skill'i iptal et",
  resolve_catalog_context: "Katalog bağlamını belirle",
  search_products: "Ürün ara",
  resolve_product_by_marketplace_ref: "İlan numarasından ürünü bul",
  resolve_product_by_url: "Linkten ürünü bul",
  get_product_details: "Ürün detayını getir",
  get_product_variants: "Varyantları getir (beden/renk)",
  compare_products: "Ürünleri karşılaştır",
  get_product_policies: "İade/kargo politikalarını getir",
  get_size_guide: "Beden tablosunu getir",
  get_listing_group_variants: "İlanın diğer varyantlarını getir",
  get_my_orders: "Müşterinin siparişlerini getir",
  get_my_order: "Sipariş detayını getir",
  get_my_addresses: "Müşterinin adreslerini getir",
  commerce_prepare_order: "Sipariş taslağı hazırla",
  commerce_prepare_cancellation: "İptal taslağı hazırla"
}

export function toolLabel(tool: string): string {
  if (TOOL_LABELS[tool]) return TOOL_LABELS[tool]
  if (tool.startsWith("action_")) return "Kanal action'ını çalıştır"
  return tool
}

/** Arguments that carry the model's own sentence, and the word to put in front of it. */
export const INTENT_LABELS: Record<string, string> = {
  query: "Aradığı",
  purpose: "Amacı",
  reason: "Gerekçesi",
  summary: "Adım özeti",
  skipReason: "Atlama gerekçesi"
}

export const ARG_LABELS: Record<string, string> = {
  referenceId: "yetenek",
  stepId: "adım",
  skillId: "skill",
  productId: "ürün",
  variantId: "varyant",
  orderId: "sipariş",
  skippedReferenceIds: "atlanan",
  attribute: "alan",
  value: "değer"
}

/**
 * `expected` codes are the flow working as designed (the host refuses a write once to get the
 * customer's confirmation), so they must not read like a failure.
 */
export const TOOL_ERROR_LABELS: Record<
  string,
  { text: string; expected?: boolean }
> = {
  CONFIRMATION_REQUIRED: {
    text: "Onay gerekiyor — host ilk çağrıyı bilerek durdurdu; ajan değerleri müşteriye onaylatacak",
    expected: true
  },
  PENDING_WRITE_CONFIRMATION: {
    text: "Önceki kayıt işlemi hâlâ müşteri onayı bekliyor",
    expected: true
  },
  WRITE_ALREADY_ATTEMPTED: { text: "Bu kayıt işlemi bu turda zaten denendi" },
  WRITE_IN_PROGRESS: { text: "Aynı kayıt işlemi şu an çalışıyor" },
  WRITE_FAILED: { text: "Kayıt işlemi dış sistemde başarısız oldu" },
  WRITE_GATE_UNAVAILABLE: { text: "Kayıt onay mekanizması bu kanalda yok" },
  NO_ACTIVE_SKILL: { text: "Aktif skill yok — önce skill başlatılmalıydı" },
  SKILL_ALREADY_ACTIVE: { text: "Zaten çalışan bir skill var" },
  SKILL_NOT_AVAILABLE: { text: "Bu skill bu kanal/rol için açık değil" },
  TARGET_SKILL_UNAVAILABLE: { text: "Geçilmek istenen skill kullanılamıyor" },
  NO_PUBLISHED_VERSION: { text: "Skill'in yayınlanmış sürümü yok" },
  NOT_THE_ACTIVE_STEP: { text: "Bu adım şu an aktif adım değil" },
  CAPABILITY_NOT_IN_ACTIVE_STEP: {
    text: "Bu yetenek aktif adımda tanımlı değil"
  },
  WRONG_CAPABILITY_KIND: {
    text: "Yanlış araçla çağrıldı (ör. okuma yeteneği yazma aracıyla)"
  },
  UNKNOWN_REFERENCE: { text: "Böyle bir yetenek numarası yok" },
  REQUIRED_CAPABILITY_NOT_RUN: {
    text: "Adım bitirilmeden önce çalışması gereken yetenek çalışmadı"
  },
  SKIP_REASON_REQUIRED: {
    text: "Atlanan yetenek için gerekçe yazılmadı"
  },
  HOP_BUDGET_EXHAUSTED: { text: "Skill geçiş limiti doldu" },
  MISSING_INPUTS: { text: "Gerekli parametreler eksik" },
  MISSING_VALUE: { text: "Değer eksik" },
  INVALID_ACTION: { text: "Geçersiz action" },
  ACTION_DATA_UNAVAILABLE: { text: "Action veri döndürmedi" },
  ID_NOT_IN_TRUSTED_CONTEXT: {
    text: "Model, bu turda görmediği bir ID uydurdu — host reddetti"
  },
  ORDERS_NOT_AVAILABLE_ON_THIS_CHANNEL: {
    text: "Bu kanalda sipariş bilgisine erişim yok"
  },
  KNOWLEDGE_UNAVAILABLE: { text: "Bilgi kaynağı kullanılamıyor" },
  KNOWLEDGE_SEARCH_DISABLED: { text: "Bilgi araması kapalı" },
  HANDOFF_TARGET_UNAVAILABLE: { text: "Devredilecek ekip/kişi bulunamadı" },
  PERSON_UNAVAILABLE: { text: "Müşteri kaydı bulunamadı" },
  DUPLICATE_PERSON: { text: "Aynı müşteri zaten kayıtlı" },
  INVALID_PHONE: { text: "Telefon numarası geçersiz" },
  INVALID_EMAIL: { text: "E-posta geçersiz" },
  MCP_SCHEMA_VIOLATION: { text: "Mağaza aracı beklenmeyen formatta döndü" },
  PROPOSAL_INCOMPLETE: { text: "Sipariş/iptal taslağı eksik" }
}

export const ACTION_LABELS: Record<string, string> = {
  answer: "Cevapladı",
  clarify: "Müşteriye soru sordu",
  handoff: "İnsana devretti",
  spam: "Spam dedi",
  no_reply: "Cevaplamamaya karar verdi"
}

export const ROLE_REASON_LABELS: Record<string, string> = {
  SINGLE_ELIGIBLE_ROLE: "bu kanalda uygun tek rol bu",
  SETTLED: "konuşma daha önce bu role sabitlenmişti",
  PROVISIONAL_DEFAULT:
    "geçici varsayılan — ajan gerekirse başka role geçebilir",
  AGENT_SELECTED: "ajan bu rolü seçti",
  SWITCHED_ON_CAPABILITY_GAIN: "daha yetenekli role geçildi",
  SWITCHED_ON_NEW_TOPIC: "yeni konu açıldı — rol değişti",
  KEPT_SETTLED: "sabit rol korundu",
  NO_ELIGIBLE_AI_ROLE: "uygun AI rolü yok"
}

export const DELIVERY_LABELS: Record<string, string> = {
  SEND: "Cevap müşteriye gönderildi",
  SEND_AND_HANDOFF: "Cevap gönderildi ve konuşma insana devredildi",
  HANDOFF_SILENT: "Cevap gönderilmeden insana devredildi",
  AI_ESCALATED: "AI konuşmayı insana yükseltti",
  SPAM: "Mesaj spam olarak işaretlendi",
  SPAM_NOT_CONFIGURED:
    "Ajan spam dedi ama spam aksiyonu ayarlı değil — işaretlenmedi",
  NO_REPLY: "Cevap gönderilmedi",
  SINGLE_REPLY_WITHHELD: "Tek cevap hakkı olan yüzeyde cevap tutuldu",
  EMPTY_REPLY_WITHHELD: "Cevap boştu — gönderilmedi"
}

export const DELIVERY_ACTION_LABELS: Record<string, string> = {
  ATTACH_AI: "AI konuşmaya bağlandı",
  SEND_MESSAGE: "mesaj gönderildi",
  BILL_AI_ANSWER: "AI cevabı faturalandı",
  CLOSE_CONVERSATION: "konuşma kapatıldı",
  RESOLVE_TICKET: "talep çözüldü",
  TRANSFER_TEAM: "ekibe devredildi",
  TRANSFER_AGENT: "temsilciye devredildi",
  MARK_SPAM: "spam işaretlendi",
  CLOSING_NOTE: "kapanış notu eklendi",
  CLOSING_TAGS: "kapanış etiketleri eklendi",
  SURVEY_SENT: "anket gönderildi",
  START_FLOW: "akış başlatıldı"
}

export const CONVERSATION_STATE_LABELS: Record<string, string> = {
  ONGOING: "konuşma sürüyor",
  RESOLVED: "konu çözüldü"
}

export const CONFIDENCE_LABELS: Record<string, string> = {
  high: "yüksek",
  medium: "orta",
  low: "düşük"
}

export const SEARCH_STEP_LABELS: Record<string, string> = {
  GET_ELASTIC_BM25_DATA: "anahtar kelime araması",
  GET_PINECONE_DATA: "anlam araması",
  FUSE_RAG_RESULTS: "sonuçları birleştirme",
  RERANK_CHUNKS: "en alakalıları seçme",
  CHECK_IMAGE_PINECONE: "görsel araması",
  HTTP_REQUEST: "dış servise istek",
  COMMERCE_MCP_TOOL_CALL: "mağaza (MCP) çağrısı",
  MARKETPLACE_REQUEST: "pazaryeri API çağrısı"
}

export const PHASE_HINTS = {
  prepare:
    "Ajan çalışmadan önce sistemin yaptıkları: rol seçimi, ürün eşleştirme, araçların hazırlanması.",
  agent:
    "Model her turda düşünür, bir araç çağırır, sonucu okur ve tekrar karar verir — cevabı yazana kadar.",
  settle: "Ajanın cevabıyla sistemin ne yaptığı: gönderim, devir, kurallar."
}

export const SURFACE_LABELS: Record<string, string> = {
  CHAT: "sohbet mesajı",
  COMMENT: "gönderi altı yorum",
  PRIVATE_REPLY: "yoruma özelden cevap",
  MARKETPLACE_QUESTION: "pazaryeri ürün sorusu",
  TICKET: "e-posta / talep"
}
